import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

class ProductPriceInventory extends Component {
  /** @type {MutationObserver | null} */
  #priceObserver = null;
  /** @type {HTMLElement | null} */
  #closestSection = null;
  #discountUpdateRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateContent);
    document.addEventListener(ThemeEvents.variantUpdate, this.updateContent);
    this.addEventListener('click', this.onClick);

    this.#observePriceChanges();
    this.#scheduleDiscountUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateContent);
    this.#closestSection = null;
    document.removeEventListener(ThemeEvents.variantUpdate, this.updateContent);
    this.removeEventListener('click', this.onClick);
    this.#priceObserver?.disconnect();
    this.#priceObserver = null;
    if (this.#discountUpdateRaf) {
      cancelAnimationFrame(this.#discountUpdateRaf);
      this.#discountUpdateRaf = 0;
    }
  }

  /**
   * Updates the combined price and inventory block on variant changes.
   * @param {VariantUpdateEvent} event
   */
  updateContent = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const selector = `product-price-inventory[data-block-id="${this.dataset.blockId}"]`;
    let newContent = event.detail.data.html.querySelector(selector);
    if (!newContent) {
      newContent = event.detail.data.html.querySelector('product-price-inventory');
    }
    if (!newContent) return;

    this.toggleAttribute('hidden', newContent.hasAttribute('hidden'));

    // Rebuild the block markup so the price and inventory state stay in sync
    // after switching between variants.
    this.innerHTML = newContent.innerHTML;
    this.updatedCallback();
    this.#scheduleDiscountUpdate();
  };

  #observePriceChanges() {
    this.#priceObserver?.disconnect();
    this.#priceObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => this.#mutationTouchesPrice(mutation))) return;
      this.#scheduleDiscountUpdate();
    });

    this.#priceObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  /**
   * Returns true when a mutation touches the price or compare-at price nodes.
   * @param {MutationRecord} mutation
   * @returns {boolean}
   */
  #mutationTouchesPrice(mutation) {
    if (mutation.type === 'attributes' || mutation.type === 'characterData') {
      return this.#nodeTouchesPrice(mutation.target);
    }

    return (
      this.#nodeTouchesPrice(mutation.target) ||
      [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.#nodeContainsPrice(node))
    );
  }

  /**
   * Returns true when a node is the price element or inside one.
   * @param {Node} node
   * @returns {boolean}
   */
  #nodeTouchesPrice(node) {
    const element = node instanceof Element ? node : node.parentElement;
    if (!element) return false;

    const priceSelector = '.product-price-inventory__line--current .price, .product-price-inventory__line--original .compare-at-price';

    return Boolean(element.matches(priceSelector) || element.querySelector(priceSelector));
  }

  /**
   * Returns true when a node contains one of the price elements.
   * @param {Node} node
   * @returns {boolean}
   */
  #nodeContainsPrice(node) {
    if (!(node instanceof Element)) return false;

    return Boolean(
      node.matches('.product-price-inventory__line--current .price, .product-price-inventory__line--original .compare-at-price') ||
        node.querySelector('.product-price-inventory__line--current .price, .product-price-inventory__line--original .compare-at-price')
    );
  }

  #scheduleDiscountUpdate() {
    if (this.#discountUpdateRaf) return;

    this.#discountUpdateRaf = requestAnimationFrame(() => {
      this.#discountUpdateRaf = 0;
      this.#updateDiscountBadge();
    });
  }

  #updateDiscountBadge() {
    const currentLine =
      this.refs.currentPriceLine instanceof HTMLElement
        ? this.refs.currentPriceLine
        : this.querySelector('.product-price-inventory__line--current');
    if (!(currentLine instanceof HTMLElement)) return;

    const originalLine =
      this.refs.originalPriceLine instanceof HTMLElement
        ? this.refs.originalPriceLine
        : this.querySelector('.product-price-inventory__line--original');

    const currentPrice = this.refs.currentPrice instanceof HTMLElement ? this.refs.currentPrice : currentLine.querySelector('.price');
    const originalPrice =
      this.refs.originalPrice instanceof HTMLElement ? this.refs.originalPrice : originalLine?.querySelector('.compare-at-price');

    if (!(originalLine instanceof HTMLElement) || !(currentPrice instanceof HTMLElement) || !(originalPrice instanceof HTMLElement)) {
      if (originalLine instanceof HTMLElement) {
        this.#setOriginalLineVisibility(originalLine, false);
      }
      const badgeOnly = currentLine.querySelector('.product-price-inventory__discount');
      if (badgeOnly instanceof HTMLElement) {
        this.#setDiscountBadge(badgeOnly, null);
      }
      return;
    }

    const originalValue = this.#readDisplayedMoney(originalPrice);
    const currentValue = this.#readDisplayedMoney(currentPrice);
    const shouldShowOriginal =
      originalValue != null && currentValue != null && originalValue > 0 && currentValue < originalValue;

    this.#setOriginalLineVisibility(originalLine, shouldShowOriginal);

    let badge = currentLine.querySelector('.product-price-inventory__discount');
    if (!(badge instanceof HTMLElement)) {
      badge = document.createElement('span');
      badge.className = 'product-price-inventory__discount';
      badge.setAttribute('ref', 'discountRate');
      badge.hidden = true;
    }

    const stockPill = currentLine.querySelector('.product-price-inventory__stock-pill');
    if (stockPill instanceof HTMLElement) {
      stockPill.insertAdjacentElement('beforebegin', badge);
    } else if (badge.parentElement !== currentLine) {
      currentLine.append(badge);
    }

    if (!shouldShowOriginal || originalValue == null || currentValue == null) {
      this.#setDiscountBadge(badge, null);
      return;
    }

    const discountRate = Math.round(((originalValue - currentValue) / originalValue) * 100);
    this.#setDiscountBadge(badge, discountRate);
  }

  /**
   * Updates the discount badge text and visibility.
   * @param {HTMLElement} badge
   * @param {number | null} discountRate
   */
  #setDiscountBadge(badge, discountRate) {
    if (discountRate == null || discountRate <= 0) {
      badge.textContent = '';
      if (!badge.hidden) {
        badge.hidden = true;
      }
      return;
    }

    const nextText = `${discountRate}% OFF`;
    if (badge.textContent !== nextText) {
      badge.textContent = nextText;
    }

    if (badge.hidden) {
      badge.hidden = false;
    }
  }

  /**
   * Reads the displayed money text from a price element and converts it to minor units.
   * @param {string | HTMLElement} target
   * @returns {number | null}
   */
  #readDisplayedMoney(target) {
    const priceElement = typeof target === 'string' ? this.querySelector(target) : target;
    if (!(priceElement instanceof HTMLElement)) return null;

    const clone = priceElement.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return null;

    clone.querySelectorAll('.wpd-ext-compare-at-price, s, [hidden]').forEach((node) => node.remove());

    const rawText = clone.textContent?.replace(/\s+/g, ' ').trim();
    if (!rawText) return null;

    const currencyCode = this.dataset.currencyCode || 'USD';
    const amount = convertMoneyToMinorUnits(rawText, currencyCode);
    return amount;
  }

  /**
   * Shows or hides the original price line.
   * @param {HTMLElement} originalLine
   * @param {boolean} shouldShow
   */
  #setOriginalLineVisibility(originalLine, shouldShow) {
    const shouldHide = !shouldShow;

    if (originalLine.hidden !== shouldHide) {
      originalLine.hidden = shouldHide;
    }

    originalLine.classList.toggle('product-price-inventory__line--hidden', shouldHide);
  }

  onClick = (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    const dialog = this.querySelector('[data-stock-availability-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (target.closest('[data-stock-availability-open]')) {
      dialog.showModal();
      return;
    }

    if (target.closest('[data-stock-availability-close]')) {
      dialog.close();
      return;
    }

    if (target === dialog) {
      const rect = dialog.getBoundingClientRect();
      const isInDialog =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isInDialog) {
        dialog.close();
      }
    }
  };
}

if (!customElements.get('product-price-inventory')) {
  customElements.define('product-price-inventory', ProductPriceInventory);
}
